import { useState, useEffect, useMemo } from "react";
import { Info, ShieldAlert } from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { useToast } from "../../components/ui/toast";
import { useAuth } from "../../context/AuthContext";
import { changePassword, updateUserPreferences } from "./settings-api";
import { TimezoneSelect, formatTimezoneLabel } from "../../components/ui/timezone-select";
import { getDetectedTimezone, isTimezoneMismatch } from "../../utils/timezone";
import { TimezoneMismatchModal } from "../../components/TimezoneMismatchModal";
import { VpnActionBlockedModal } from "../../components/VpnActionBlockedModal";
import { VpnDetailsModal } from "../../components/VpnDetailsModal";

export function ProfileTab() {
  const toast = useToast();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const {
    token,
    user,
    refreshUser,
    vpnDetected = false,
    networkInfo = null,
    recheckNetworkConnection,
    recheckingNetwork = false,
  } = useAuth() as any;

  const [username, setUsername] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [orgName, setOrgName] = useState("");
  const [timezone, setTimezone] = useState(user?.timezone || "Asia/Kolkata");

  const detectedTimezone = useMemo(() => getDetectedTimezone(), []);

  useEffect(() => {
    if (typeof recheckNetworkConnection === "function") {
      recheckNetworkConnection().catch(() => {});
    }
  }, [recheckNetworkConnection]);
  const [showMismatchModal, setShowMismatchModal] = useState(false);

  // VPN blocked action modal state
  const [blockedModalOpen, setBlockedModalOpen] = useState(false);
  const [blockedActionTitle, setBlockedActionTitle] = useState("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.name || "");
      setEmail(user.email || "");
      if (user.timezone) {
        setTimezone(user.timezone);
      }
    }
  }, [user]);

  const [tempUsername, setTempUsername] = useState(username);
  const [tempEmail, setTempEmail] = useState(email);
  const [tempOrgName, setTempOrgName] = useState(orgName);
  const [tempTimezone, setTempTimezone] = useState(timezone);
  const [savingProfile, setSavingProfile] = useState(false);
  
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const executeSaveProfile = async (targetTimezone: string) => {
    if (vpnDetected) {
      setBlockedActionTitle("Saving Profile & Timezone Preferences");
      setBlockedModalOpen(true);
      return;
    }

    try {
      setSavingProfile(true);
      if (token) {
        await updateUserPreferences({ timezone: targetTimezone }, token);
        if (typeof refreshUser === "function") {
          await refreshUser(token, { force: true });
        }
      }
      setUsername(tempUsername);
      setEmail(tempEmail);
      setOrgName(tempOrgName);
      setTimezone(targetTimezone);
      toast.success("Profile updated successfully");
      setEditModalOpen(false);
    } catch (err: any) {
      const errDetail = err.response?.data?.detail;
      const errCode = typeof errDetail === "object" ? errDetail?.code : null;

      if (err.response?.status === 403 && (errCode === "VPN_MUTATION_BLOCKED" || vpnDetected)) {
        setBlockedActionTitle("Saving Profile & Timezone Preferences");
        setBlockedModalOpen(true);
      } else if (err.response?.status === 404) {
        // Fallback for older dev environments where /preferences is not yet deployed
        setUsername(tempUsername);
        setEmail(tempEmail);
        setOrgName(tempOrgName);
        setTimezone(targetTimezone);
        toast.warning("Preferences endpoint currently syncing on server. Timezone updated locally for this session.");
        setEditModalOpen(false);
      } else {
        toast.danger(typeof errDetail === "string" ? errDetail : "Failed to update profile");
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const [mismatchConfirmed, setMismatchConfirmed] = useState(false);

  const handleTimezoneChange = (newTz: string) => {
    const canonicalTz = newTz === "Asia/Calcutta" ? "Asia/Kolkata" : newTz;
    setTempTimezone(canonicalTz);
    setMismatchConfirmed(false);
    if (canonicalTz !== timezone && isTimezoneMismatch(canonicalTz, detectedTimezone)) {
      setShowMismatchModal(true);
    }
  };

  const handleSaveProfile = async () => {
    if (vpnDetected) {
      setBlockedActionTitle("Saving Profile & Timezone Preferences");
      setBlockedModalOpen(true);
      return;
    }

    if (
      tempTimezone !== timezone &&
      isTimezoneMismatch(tempTimezone, detectedTimezone) &&
      !mismatchConfirmed
    ) {
      setShowMismatchModal(true);
      return;
    }
    await executeSaveProfile(tempTimezone);
  };

  const handleChangePassword = async () => {
    if (vpnDetected) {
      setBlockedActionTitle("Changing Account Password");
      setBlockedModalOpen(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.danger("Passwords do not match");
      return;
    }
    if (!token) {
      toast.danger("Authentication required");
      return;
    }
    try {
      setIsChangingPassword(true);
      await changePassword(currentPassword, newPassword, token);
      toast.success("Password changed successfully");
      setPasswordModalOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      const errDetail = err.response?.data?.detail;
      const errCode = typeof errDetail === "object" ? errDetail?.code : null;

      if (err.response?.status === 403 && (errCode === "VPN_MUTATION_BLOCKED" || vpnDetected)) {
        setBlockedActionTitle("Changing Account Password");
        setBlockedModalOpen(true);
      } else {
        toast.danger(typeof errDetail === "string" ? errDetail : "Failed to change password");
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleOpenEdit = () => {
    setTempUsername(username);
    setTempEmail(email);
    setTempOrgName(orgName);
    setTempTimezone(timezone);
    setEditModalOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-base font-semibold text-ink">Personal Profile</h2>
            {vpnDetected && (
              <button
                type="button"
                onClick={() => setDetailsModalOpen(true)}
                className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800 dark:border-amber-800 dark:bg-amber-950/70 dark:text-amber-300 hover:bg-amber-100"
              >
                <ShieldAlert className="h-3 w-3" />
                <span>Read-Only Mode (VPN Detected)</span>
              </button>
            )}
          </div>
          <p className="text-xs text-ink-3">View and edit your personal information.</p>
        </div>
        <button
          onClick={handleOpenEdit}
          className="rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-hover"
        >
          {vpnDetected ? "View / Edit Profile" : "Edit Profile"}
        </button>
      </div>

      {vpnDetected ? (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50/90 px-4 py-3 text-xs text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
          <div className="flex items-start gap-2.5">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <span className="font-semibold">VPN Connection Active:</span> You are currently browsing in Read-Only Mode. All personal details and timezones can be reviewed freely, but modifications require disconnecting from VPN or requesting admin authorization.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setDetailsModalOpen(true)}
            className="shrink-0 underline font-semibold text-amber-800 hover:text-amber-950 dark:text-amber-300"
          >
            Diagnostics
          </button>
        </div>
      ) : (
        <p className="flex items-start gap-2 rounded-lg border border-brand/30 bg-brand-soft px-4 py-3 text-sm text-ink-2">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          Manage your personal account details, global timezone, and security settings here.
        </p>
      )}

      <div className="overflow-hidden rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524]">
        <table className="w-full text-sm">
          <thead className="bg-soft text-ink-3">
            <tr>
              <th className="px-4 py-3 text-left font-mono text-xs font-semibold uppercase tracking-wider w-1/3">Field</th>
              <th className="px-4 py-3 text-left font-mono text-xs font-semibold uppercase tracking-wider">Value</th>
              <th className="px-4 py-3 text-right font-mono text-xs font-semibold uppercase tracking-wider w-32">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-line hover:bg-soft/50">
              <td className="px-4 py-3.5 font-semibold text-ink">Username</td>
              <td className="px-4 py-3.5 text-ink-2">{username || "—"}</td>
              <td className="px-4 py-3.5 text-right"></td>
            </tr>
            <tr className="border-t border-line hover:bg-soft/50">
              <td className="px-4 py-3.5 font-semibold text-ink">Email</td>
              <td className="px-4 py-3.5 text-ink-2">{email || "—"}</td>
              <td className="px-4 py-3.5 text-right"></td>
            </tr>
            <tr className="border-t border-line hover:bg-soft/50">
              <td className="px-4 py-3.5 font-semibold text-ink">Timezone</td>
              <td className="px-4 py-3.5 text-ink-2">{formatTimezoneLabel(timezone)}</td>
              <td className="px-4 py-3.5 text-right"></td>
            </tr>
            <tr className="border-t border-line hover:bg-soft/50">
              <td className="px-4 py-3.5 font-semibold text-ink">Organisation Name</td>
              <td className="px-4 py-3.5 text-ink-2">{orgName || "—"}</td>
              <td className="px-4 py-3.5 text-right"></td>
            </tr>
            <tr className="border-t border-line hover:bg-soft/50">
              <td className="px-4 py-3.5 font-semibold text-ink">Password</td>
              <td className="px-4 py-3.5 text-ink-2">••••••••</td>
              <td className="px-4 py-3.5 text-right">
                <button
                  onClick={() => setPasswordModalOpen(true)}
                  className="rounded-lg border border-line bg-soft px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-line"
                >
                  Change
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {editModalOpen && (
        <Modal
          title={
            <div className="flex items-center gap-2">
              <span>Edit Profile</span>
              {vpnDetected && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                  Read-Only Mode
                </span>
              )}
            </div>
          }
          onClose={() => setEditModalOpen(false)}
          width="max-w-md"
          footer={
            <>
              <button onClick={() => setEditModalOpen(false)} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">Cancel</button>
              <button
                disabled={savingProfile}
                onClick={handleSaveProfile}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
                  vpnDetected
                    ? "bg-amber-600 hover:bg-amber-700"
                    : "bg-brand hover:bg-brand-hover"
                } disabled:opacity-50`}
              >
                {savingProfile ? "Saving..." : vpnDetected ? "Save Changes (Read-Only)" : "Save Changes"}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            {vpnDetected && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/50 dark:text-amber-200">
                ⚠️ <strong>VPN Detected:</strong> Saving profile or timezone preferences will be blocked while connected to a VPN.
              </div>
            )}
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Username</span>
              <input
                value={tempUsername} onChange={(e) => setTempUsername(e.target.value)}
                placeholder="e.g., demo@gmail.com"
                className="w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Email</span>
              <input
                type="email"
                value={tempEmail} onChange={(e) => setTempEmail(e.target.value)}
                placeholder="e.g., you@company.com"
                className="w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </label>
            <div>
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Timezone Preference</span>
              <TimezoneSelect
                value={tempTimezone}
                onChange={handleTimezoneChange}
                ariaLabel="Select personal timezone"
              />
            </div>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Organisation Name</span>
              <input
                value={tempOrgName} onChange={(e) => setTempOrgName(e.target.value)}
                placeholder="e.g., TechLadder AI"
                className="w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </label>
          </div>
        </Modal>
      )}

      {passwordModalOpen && (
        <Modal
          title={
            <div className="flex items-center gap-2">
              <span>Change Password</span>
              {vpnDetected && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                  Read-Only Mode
                </span>
              )}
            </div>
          }
          onClose={() => setPasswordModalOpen(false)}
          width="max-w-md"
          footer={
            <>
              <button onClick={() => setPasswordModalOpen(false)} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">Cancel</button>
              <button
                disabled={!currentPassword || !newPassword || !confirmPassword || isChangingPassword}
                onClick={handleChangePassword}
                className={(!currentPassword || !newPassword || !confirmPassword || isChangingPassword) ? "cursor-not-allowed rounded-lg bg-line px-4 py-2 text-sm font-semibold text-ink-3" : "rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover"}
              >
                {isChangingPassword ? "Updating..." : "Update Password"}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            {vpnDetected && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/50 dark:text-amber-200">
                ⚠️ <strong>VPN Detected:</strong> Password updates cannot be executed in Read-Only mode.
              </div>
            )}
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Current Password</span>
              <input
                type="password"
                value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">New Password</span>
              <input
                type="password"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Confirm New Password</span>
              <input
                type="password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
            </label>
          </div>
        </Modal>
      )}

      {/* Timezone mismatch confirmation modal */}
      <TimezoneMismatchModal
        isOpen={showMismatchModal}
        selectedTimezone={tempTimezone}
        detectedTimezone={detectedTimezone}
        onKeepSelected={() => {
          setMismatchConfirmed(true);
          setShowMismatchModal(false);
        }}
        onUseDetected={() => {
          setTempTimezone(detectedTimezone);
          setMismatchConfirmed(true);
          setShowMismatchModal(false);
        }}
      />

      {/* VPN action blocked warning modal */}
      <VpnActionBlockedModal
        isOpen={blockedModalOpen}
        onClose={() => setBlockedModalOpen(false)}
        actionTitle={blockedActionTitle || "Action Blocked: VPN Detected"}
        onOpenDetails={() => setDetailsModalOpen(true)}
        onRecheck={recheckNetworkConnection}
        rechecking={recheckingNetwork}
      />

      {/* VPN detailed diagnostics modal */}
      <VpnDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        networkInfo={networkInfo}
        userEmail={user?.email}
        onRecheck={recheckNetworkConnection}
        rechecking={recheckingNetwork}
      />
    </div>
  );
}

export default ProfileTab;
